import { computed, ref, unref, watch } from "vue";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore";

import {
  ADMIN_DRAFT_QUEUE_DEFAULT_LABEL,
  ADMIN_DRAFT_QUEUE_DESTINATION_EDITOR,
  ADMIN_DRAFT_QUEUE_DESTINATION_PREVIEW,
  ADMIN_DRAFT_QUEUE_KIND_FLASHCARD,
  buildAdminDraftQueueRoute,
  buildAdminDraftQueueRows,
  buildAdminDraftQueueRowsFromOrderedIds,
  canAdvanceAdminDraftQueueAfterRemoval,
  getAdminDraftQueueBankHint,
  getNextAdminDraftQueueStateAfterRemoval,
  loadAdminDraftQueueContext,
  navigateAdminDraftQueuePreviewRoute,
  normalizeQueueId,
  resolveAdminDraftQueueExitPath,
  saveAdminDraftQueueContext,
  updateAdminDraftQueueContextOrderedIds,
} from "@/utils/adminDraftQueue.mjs";

const ADMIN_CONTENT_ROWS_COLLECTION = "admin_content_rows";
const FLASHCARD_MISSING_CONTEXT_NOTICE =
  "Saved queue context was not available. Return to Flashcards admin and reopen the visual editor to continue the queue.";

const getSessionStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export function useAdminDraftQueue({
  kind,
  bankId,
  currentId,
  route,
  router,
  contentEditor = "default",
  destination = ADMIN_DRAFT_QUEUE_DESTINATION_EDITOR,
  enabled = true,
  navigateDocument,
}) {
  const firestore = getFirestore();

  const rows = ref([]);
  const context = ref(null);
  const label = ref(ADMIN_DRAFT_QUEUE_DEFAULT_LABEL);
  const notice = ref("");
  const loading = ref(false);
  const error = ref("");
  let refreshRequestId = 0;
  let refreshPromise = null;
  let refreshSignature = "";

  const kindValue = computed(() => normalizeQueueId(unref(kind)));
  const bankIdValue = computed(() => normalizeQueueId(unref(bankId)));
  const currentIdValue = computed(() => normalizeQueueId(unref(currentId)));
  const queueKey = computed(() => normalizeQueueId(route.query.draftQueue));
  const destinationValue = computed(() => normalizeQueueId(unref(destination)));
  const enabledValue = computed(() => Boolean(unref(enabled)));
  const isPreviewDestination = computed(
    () => destinationValue.value === ADMIN_DRAFT_QUEUE_DESTINATION_PREVIEW,
  );

  const currentIndex = computed(() =>
    rows.value.findIndex((row) => row.id === currentIdValue.value),
  );
  const currentRow = computed(() => rows.value[currentIndex.value] || null);
  const currentPositionLabel = computed(() =>
    currentIndex.value >= 0
      ? `${currentIndex.value + 1} of ${rows.value.length}`
      : `0 of ${rows.value.length}`,
  );
  const canGoPrevious = computed(
    () => rows.value.length > 1 && currentIndex.value > 0,
  );
  const canGoNext = computed(
    () =>
      rows.value.length > 1 &&
      currentIndex.value >= 0 &&
      currentIndex.value < rows.value.length - 1,
  );
  const canAdvanceAfterRemoval = computed(() =>
    canAdvanceAdminDraftQueueAfterRemoval({
      rows: rows.value,
      currentId: currentIdValue.value,
      forwardOnly: isPreviewDestination.value,
    }),
  );
  const getExitPath = (fallbackPath = "") =>
    resolveAdminDraftQueueExitPath({
      context: context.value,
      fallbackPath,
    });

  const loadContentRows = async () => {
    const snapshot = await getDocs(
      query(
        collection(firestore, ADMIN_CONTENT_ROWS_COLLECTION),
        where("bankId", "==", bankIdValue.value),
        where("rowState", "==", "draft"),
      ),
    );

    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data() || {};
      return {
        ...data,
        id: data.id || data.entityId || docSnap.id,
      };
    });
  };

  const buildFlashcardQueueRows = (loadedContext = null) => {
    const orderedIds = Array.isArray(loadedContext?.orderedIds)
      ? loadedContext.orderedIds
      : [];
    const hasStrictSavedOrder =
      isPreviewDestination.value &&
      loadedContext &&
      Array.isArray(loadedContext.orderedIds);
    const queueIds =
      orderedIds.length || hasStrictSavedOrder
        ? orderedIds
        : [currentIdValue.value].filter(Boolean);

    return buildAdminDraftQueueRowsFromOrderedIds(queueIds, {
      bankHintsById: loadedContext?.bankHintsById,
    });
  };

  const getRefreshSignature = () =>
    [
      queueKey.value,
      bankIdValue.value,
      kindValue.value,
      destinationValue.value,
    ].join("\u0000");

  const reset = () => {
    refreshRequestId += 1;
    rows.value = [];
    context.value = null;
    label.value = ADMIN_DRAFT_QUEUE_DEFAULT_LABEL;
    notice.value = "";
    loading.value = false;
    error.value = "";
  };

  const runRefresh = async () => {
    if (!enabledValue.value) {
      reset();
      return false;
    }

    const requestId = ++refreshRequestId;
    loading.value = true;
    error.value = "";
    notice.value = "";

    try {
      let loadedContext = queueKey.value
        ? loadAdminDraftQueueContext({
            key: queueKey.value,
            storage: getSessionStorage(),
          })
        : null;
      const nextLabel = loadedContext?.label || ADMIN_DRAFT_QUEUE_DEFAULT_LABEL;
      const nextNotice =
        queueKey.value && !loadedContext
          ? kindValue.value === ADMIN_DRAFT_QUEUE_KIND_FLASHCARD
            ? FLASHCARD_MISSING_CONTEXT_NOTICE
            : "Saved queue context was not available. Showing all drafts by default."
          : "";
      const nextRows =
        kindValue.value === ADMIN_DRAFT_QUEUE_KIND_FLASHCARD
          ? buildFlashcardQueueRows(loadedContext)
          : buildAdminDraftQueueRows({
              rows: await loadContentRows(),
              orderedIds: loadedContext?.orderedIds || [],
              strictOrderedIds:
                isPreviewDestination.value && Boolean(loadedContext),
            });

      if (requestId !== refreshRequestId) return false;

      if (isPreviewDestination.value && loadedContext) {
        const prunedContext = updateAdminDraftQueueContextOrderedIds({
          context: loadedContext,
          orderedIds: nextRows.map((row) => row.id).filter(Boolean),
        });
        if (
          JSON.stringify(prunedContext?.orderedIds) !==
          JSON.stringify(loadedContext.orderedIds)
        ) {
          saveAdminDraftQueueContext({
            key: queueKey.value,
            context: prunedContext,
            storage: getSessionStorage(),
          });
          loadedContext = prunedContext;
        }
      }

      context.value = loadedContext;
      label.value = nextLabel;
      notice.value = nextNotice;
      rows.value = nextRows;
      return true;
    } catch (loadError) {
      if (requestId !== refreshRequestId) return false;

      console.error("Failed to load admin draft queue:", loadError);
      error.value = loadError?.message || "Failed to load draft queue.";
      rows.value = [];
      context.value = null;
      label.value = ADMIN_DRAFT_QUEUE_DEFAULT_LABEL;
      return false;
    } finally {
      if (requestId === refreshRequestId) {
        loading.value = false;
      }
    }
  };

  const refresh = () => {
    refreshSignature = getRefreshSignature();
    refreshPromise = runRefresh();
    return refreshPromise;
  };

  const ensureRefreshed = () =>
    refreshPromise && refreshSignature === getRefreshSignature()
      ? refreshPromise
      : refresh();

  const navigateToId = (id) => {
    const targetId = normalizeQueueId(id);
    if (!targetId) return false;

    const targetRoute = buildAdminDraftQueueRoute({
      kind: kindValue.value,
      bankId: bankIdValue.value,
      id: targetId,
      queueKey: queueKey.value,
      contentEditor,
      destination: destinationValue.value,
      bankHint: getAdminDraftQueueBankHint({
        context: context.value,
        id: targetId,
        fallbackBank: route.query.bank,
      }),
    });

    if (isPreviewDestination.value) {
      return navigateAdminDraftQueuePreviewRoute({
        router,
        route: targetRoute,
        ...(typeof navigateDocument === "function"
          ? { navigate: navigateDocument }
          : {}),
      });
    }

    return router.push(targetRoute);
  };

  const goPrevious = () => {
    if (!canGoPrevious.value) return false;
    return navigateToId(rows.value[currentIndex.value - 1]?.id);
  };

  const goNext = () => {
    if (!canGoNext.value) return false;
    return navigateToId(rows.value[currentIndex.value + 1]?.id);
  };

  const jumpTo = (id) => navigateToId(id);

  const persistCurrentQueueRows = (nextRows) => {
    if (!queueKey.value || !context.value) return;

    const nextContext = updateAdminDraftQueueContextOrderedIds({
      context: context.value,
      orderedIds: nextRows.map((row) => row.id).filter(Boolean),
    });
    context.value = nextContext;
    saveAdminDraftQueueContext({
      key: queueKey.value,
      context: nextContext,
      storage: getSessionStorage(),
    });
  };

  const consumeCurrent = () => {
    refreshRequestId += 1;
    loading.value = false;

    const state = getNextAdminDraftQueueStateAfterRemoval({
      rows: rows.value,
      currentId: currentIdValue.value,
      forwardOnly: isPreviewDestination.value,
    });
    rows.value = state.rows;
    persistCurrentQueueRows(state.rows);
    if (state.notice) {
      notice.value = state.notice;
    }

    return state;
  };

  const removeCurrentAndAdvance = async ({ fallbackPath = "" } = {}) => {
    const state = consumeCurrent();
    if (state.nextId) {
      const navigationResult = await navigateToId(state.nextId);
      return navigationResult !== false;
    }
    if (fallbackPath) {
      await router.push(fallbackPath);
      return true;
    }
    return false;
  };

  watch(
    [queueKey, bankIdValue, kindValue, destinationValue, enabledValue],
    () => {
      if (!enabledValue.value) {
        refreshPromise = null;
        refreshSignature = "";
        reset();
        return;
      }

      void refresh();
    },
    { immediate: true },
  );

  return {
    rows,
    currentIndex,
    currentRow,
    currentPositionLabel,
    canGoPrevious,
    canGoNext,
    canAdvanceAfterRemoval,
    label,
    notice,
    loading,
    error,
    getExitPath,
    refresh,
    ensureRefreshed,
    goPrevious,
    goNext,
    jumpTo,
    navigateToId,
    consumeCurrent,
    removeCurrentAndAdvance,
  };
}
